import {
  pxTovW,
  pxToRem,
  IStyles,
  QuestionContainerWithSolution,
  deserify,
  getTeacherSubjectName,
  useCommonServiceClientContext,
  theme,
  IconWrapper,
  cloneObject,
  ActionsPopup,
  QuestionSourceTags,
} from '@geneo2-web/shared-ui';
import {
  AccordionSummary,
  Accordion,
  Box,
  Typography,
  useMediaQuery,
  AccordionDetails,
  Button,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../reduxStore/reduxHooks';
import { Question } from '@protos/content_management/content.db_pb';
import {
  MultiSubjectSection,
  QuestionTypeMarks,
  SectionQuestionMetaData,
  TaskCreationStatusEnum,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import type { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  getQuestionNumberFromIndex,
  questionHasImage,
  useAddOrReplaceQPGQuestionMultiSubject,
  useShowMediaClickMultiSubject,
  useDeleteQPGQuestionMultiSubject,
} from '../../../QuestionPaperGenerator/ReviewQuestionPaper/utils';
import { InteractiveButtons } from '../../../QuestionPaperGenerator/ReviewQuestionPaper/InteractiveButtons';
import {
  AssessmentCreationEnum,
  SubSectionQuestionType,
  SubSectionTypeV2,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setQPGQuestionModuleMap, // qpg_question_module_map : QuestionModuleMap[]
  setQPGCreatedSectionTest,
  setSelectedQuestionIndexForAlternateQuestion, // selected_question_index_for_alternate : number | undefined
  setSelectedQuestionTypeForAlternateQuestion, // selected_question_type_for_alternate : Question_QuestionEnum | undefined
  setQPGSelectedSubSectionIndex,
  setQPGContent, // selected_section_index_for_alternate : number | undefined
  setQPGSubsectionData,
} from '../../../reducer/assessmentFlow.slice';
import { setToastInfo } from 'apps/teacher/src/pages/Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
// import { QPG } from 'apps/teacher/src/routeHandling/RoutesNomenclature';
import { QPG_QUESTION_BANK } from '../../../../../routeHandling/RoutesNomenclature';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Timestamp } from '@bufbuild/protobuf';
import {
  getUniqueQuestionIdsFromQpgContent,
  getDisplayQuestionsForMeta,
  getAllQuestionIdsFromMeta,
  flattenMetaForMutation,
  buildMetaFromDisplayList,
  getChildQuestionsForMeta,
  getGroupedQuestionHeaderLabel,
  getGroupedQuestionHeaderBody,
  isPassageSectionEnabled,
  toRomanNumeral,
  getSubSectionMarksTotal,
  formatMarksDisplay,
  seedGroupedQuestionHeadingsOnSections,
  getSubSectionAttemptAnyInstruction,
} from '../../../utils/QPGv2.utils';
import { getCloudUploadPreviewSrc } from '../../../../../utils/commonUtils';
import { GroupedHeaderEditor } from './GroupedHeaderEditor';

/** 1-based question number across earlier sections, then earlier subsections. */
const getQuestionNumberFromMultiSubject = (props: {
  sectionIndex: number;
  subSectionIndex: number;
  questionIndexInSubSection: number;
  multiSubjectSections: MultiSubjectSection[];
}): number => {
  const {
    sectionIndex,
    subSectionIndex,
    questionIndexInSubSection,
    multiSubjectSections,
  } = props;
  let n = 0;
  for (let s = 0; s < sectionIndex; s++) {
    const section = multiSubjectSections[s];
    if (!section?.multiSubjectSubSections?.length) continue;
    for (const sub of section.multiSubjectSubSections) {
      n += sub.subSectionMetaData?.length ?? 0;
    }
  }
  const currentSection = multiSubjectSections[sectionIndex];
  if (currentSection?.multiSubjectSubSections) {
    for (let ss = 0; ss < subSectionIndex; ss++) {
      n +=
        currentSection.multiSubjectSubSections[ss]?.subSectionMetaData
          ?.length ?? 0;
    }
    n += questionIndexInSubSection;
  }
  return n + 1;
};

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(15) },
    // backgroundColor: '#ffffff',
    // borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    // border: '1px solid #E0E2E4',
    // paddingX: { xs: pxToRem(20), md: pxTovW(20) },
    // paddingY: { xs: pxToRem(16), md: pxTovW(16) },
    marginY: { xs: pxToRem(10), md: pxTovW(40) },
  },
  subjectSectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  ellipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  accordion: {
    border: '1px solid #E0E2E4',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    overflow: 'hidden',
    // remove default MUI line
    '&::before': {
      display: 'none',
    },
    // remove double borders between items
    '&:not(:last-of-type)': {
      borderBottom: 'none',
    },
    // TOP radius
    '&:first-of-type': {
      borderTopLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderTopRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
    // BOTTOM radius ⭐ FIX
    '&:last-of-type': {
      borderBottomLeftRadius: { xs: pxToRem(15), md: pxTovW(15) },
      borderBottomRightRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      justifyContent: 'space-between',
      // alignItems: 'center',
      width: '100%',
      margin: 0,
      padding: { xs: pxToRem(10), md: pxTovW(20) },
      gap: { xs: pxToRem(20), md: pxTovW(0) },
    },
  },
  subSectionDetails: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    flexWrap: 'wrap',
    alignItems: { xs: 'flex-start', md: 'center' },
    // alignItems: { xs: 'unset', md: 'center' },
    gap: { xs: pxToRem(10), md: pxTovW(30) },
    marginTop: { xs: pxToRem(10), md: pxTovW(10) },
  },
  detailsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
  },
  addMoreButton: {
    background: '#C1F8DA',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #0AA34F',
    paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    height: 'max-content',
    // height: { xs: pxToRem(28), md: pxTovW(57) },
  },
  accordionDetails: {
    paddingY: { xs: pxToRem(10), md: pxTovW(25) },
    paddingX: { xs: pxToRem(10), md: pxTovW(25) },
    borderTop: '2px solid #E0E2E4',
  },
  passageBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(12), md: pxTovW(16) },
    marginBottom: { xs: pxToRem(16), md: pxTovW(24) },
  },
  passageImage: {
    width: '100%',
    // maxHeight: { xs: pxToRem(220), md: pxTovW(280) },
    objectFit: 'cover',
    borderRadius: { xs: pxToRem(12), md: pxTovW(16) },
    display: 'block',
  },
  passageText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#1D1D1D',
  },
  instructionText: {
    fontFamily: 'Poppins',
    marginTop: { xs: pxToRem(15), md: pxTovW(15) },
    marginBottom: { xs: pxToRem(40), md: pxTovW(40) },
    fontSize: { xs: pxToRem(12), md: 'unset' },
  },
  subSectionHeading: {
    borderRadius: { xs: pxToRem(28), md: pxTovW(28) },
    backgroundColor: '#E1F2FF',
    paddingX: { xs: pxToRem(25), md: pxTovW(25) },
    paddingY: { xs: pxToRem(10), md: pxTovW(10) },
    width: 'max-content',
    marginX: 'auto',
    marginY: { xs: pxToRem(15), md: pxTovW(30) },
  },
  groupedHeaderReadOnly: {
    flex: 1,
    fontWeight: 'bold',
    fontSize: {
      xs: pxToRem(16),
      md: pxTovW(24),
    },
    marginBottom: {
      xs: pxToRem(10),
      md: pxTovW(10),
    },
  },
};

interface IProps {
  setUpdatingAssessment: Dispatch<SetStateAction<boolean>>;
  handleSave: (props: {
    createAssessment?: boolean;
    editAssessmentCreationFlow?: 'chapter' | 'bloom' | 'ques-type';
    contentOverride?: AssessmentContent;
  }) => Promise<void>;
  skipAutosaveForHeadingRef: MutableRefObject<boolean>;
}
export const QPGReviewPaperSection = (props: IProps) => {
  const { setUpdatingAssessment, handleSave, skipAutosaveForHeadingRef } =
    props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const {
    qpg_content,
    qpg_selected_section_number,
    qpg_format_details,
    qpg_subsection_data,
  } = deserify(useAppSelector((state) => state.assessment));

  const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);
  const [deleteQuestionPopupState, setDeleteQuestionPopupState] = useState<
    | {
        sectionIndex: number;
        subSectionIndex: number;
        questionIndex?: number;
        altIdx?: number;
      }
    | undefined
  >(undefined);

  // First accordion open by default; only one open at a time
  const [expandedAccordion, setExpandedAccordion] = useState<number>(0);

  const qpgAssessmentQuestions =
    qpg_content?.assessmentContent?.map((ques) => new Question(ques)) || [];
  const qpgQuestionsSequenceInfo = new TaskInfo(
    qpg_content?.questionsSequenceInfo
  );
  const taskQuestionInfo = qpgQuestionsSequenceInfo.taskQuestionInfo || [];
  const qpgQuestionTypeMarks = qpg_content?.questionTypeAndMarks || [];
  const qpgAssessmentTask = new AssessmentTask(qpg_content?.assessment);
  const taskCreationStatus = qpgAssessmentTask.taskCreationStatus;
  const assessmentContent = qpg_content?.assessmentContent || [];

  const sectionToShow = qpg_content?.multiSubjectSections.find(
    (section) => section.sectionNo === qpg_selected_section_number
  );

  const multiSubjectSubSections = sectionToShow?.multiSubjectSubSections || [];

  // Persist default displayed group headings so submit includes them even if never edited.
  useEffect(() => {
    if (!qpg_content?.multiSubjectSections?.length) return;
    if (
      taskCreationStatus ===
      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
    ) {
      return;
    }
    const { sections, changed } = seedGroupedQuestionHeadingsOnSections(
      qpg_content.multiSubjectSections
    );
    if (!changed) return;
    const updated = cloneObject(qpg_content);
    updated.multiSubjectSections = sections;
    dispatch(setQPGContent(updated));
    if (qpg_subsection_data?.length) {
      const selectedSection = sections.find(
        (section) => section.sectionNo === qpg_selected_section_number
      );
      dispatch(
        setQPGSubsectionData(
          qpg_subsection_data.map((sub, subIdx) => {
            const metaList =
              selectedSection?.multiSubjectSubSections?.[subIdx]
                ?.subSectionMetaData ?? [];
            return new SubSectionTypeV2({
              ...sub,
              subSectionQuestions: sub.subSectionQuestions.map((q, qi) => {
                const heading = metaList[qi]?.groupQuestionHeading;
                if (!q.isGrouped || !heading?.trim()) return q;
                if (q.groupQuestionHeading?.trim()) return q;
                return new SubSectionQuestionType({
                  ...q,
                  groupQuestionHeading: heading,
                });
              }),
            });
          })
        )
      );
    }
  }, [
    qpg_content?.assessment?.assessmentId,
    qpg_selected_section_number,
    taskCreationStatus,
  ]);

  // --------------------------------------------------------------

  const getFormattedQuestionFromId = (id: string) => {
    const ques = assessmentContent?.find((ques) => ques.questionId == id);
    if (ques) {
      return ques;
    }
    return undefined;
    // const taskInfo = taskQuestionInfo.find((info) => info.questionId == id);
    // if (ques?.question?.model.value?.commonQuestionContent) {
    //   ques.question.model.value.commonQuestionContent.marks = [
    //     getMarksForQuestionId(id),
    //   ];
    //   if (typeof taskInfo?.estimatedTimeInMin === 'number') {
    //     ques.question.model.value.commonQuestionContent.time =
    //       taskInfo.estimatedTimeInMin * 60;
    //   }
    // }
    // return ques;
  };

  // --------------------------------------------------------------

  const questionsList = (includeChildQuestions = false) =>
    multiSubjectSubSections
      .map((subSection) =>
        subSection.subSectionMetaData.map((quesList) => [
          getFormattedQuestionFromId(quesList.questionId),
          ...(includeChildQuestions
            ? getChildQuestionsForMeta(quesList).map((child) =>
                getFormattedQuestionFromId(child.questionId)
              )
            : []),
        ])
      )
      .flat()
      .flat()
      .filter((val) => val !== undefined) as Question[];
  // questionSectionMetaInfo
  //   .map((info) =>
  //     info.sectionQuestionsInfo.map((quesList) => [
  //       getFormattedQuestionFromId(quesList.questionId),
  //       ...(includeAlternateQuestions
  //         ? quesList.alternateQuestions.map((altQuestion) =>
  //             getFormattedQuestionFromId(altQuestion.questionId)
  //           )
  //         : []),
  //     ])
  //   )
  //   .flat()
  //   .flat()
  //   .filter((val) => val !== undefined) as Question[];
  const questionsListWithAlternateQuestions = questionsList(true);
  // console.log(
  //   'questionsListWithAlternateQuestions:',
  //   questionsListWithAlternateQuestions
  // );
  const questionModuleMap = qpg_content?.questionModuleMap || [];
  const addOrReplaceQuestion = useAddOrReplaceQPGQuestionMultiSubject();
  const showMediaClick = useShowMediaClickMultiSubject();
  const deleteQuestion = useDeleteQPGQuestionMultiSubject();
  // --------------------------------------------------------------

  // Handler for drag-and-drop
  const onDragEnd = (
    result: any,
    sectionIndex?: number,
    subSectionIndex?: number
  ) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    // Level 1: Reorder questions within a subSection (update qpg_created_section_test)
    if (
      type === 'MAIN_BOX' &&
      typeof sectionIndex === 'number' &&
      typeof subSectionIndex === 'number' &&
      qpg_content?.multiSubjectSections
    ) {
      const section = qpg_content.multiSubjectSections[sectionIndex];
      const subSection = section?.multiSubjectSubSections?.[subSectionIndex];
      const metaData = subSection?.subSectionMetaData;
      if (!section || !subSection || !metaData?.length) return;

      const updated = cloneObject(qpg_content);
      const sectionCopy = updated?.multiSubjectSections[sectionIndex];
      const subSectionCopy =
        sectionCopy?.multiSubjectSubSections?.[subSectionIndex];
      if (!subSectionCopy?.subSectionMetaData) return;

      const list = [...subSectionCopy.subSectionMetaData].sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
      );
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      list.forEach((q, idx) => {
        if (q) q.sequence = idx;
      });
      subSectionCopy.subSectionMetaData = list;
      sectionCopy.multiSubjectSubSections[subSectionIndex] = subSectionCopy;
      updated.multiSubjectSections[sectionIndex] = sectionCopy;
      dispatch(setQPGContent(updated));
      // dispatch(setQPGCreatedSectionTest(updated));
      return;
    }

    // Level 2: Reorder alternates within a question (update qpg_created_section_test)
    if (
      type.startsWith('ALTERNATE_QUESTION') &&
      qpg_content?.multiSubjectSections
    ) {
      // type: ALTERNATE_QUESTION-<sectionIndex>-<subSectionIndex>-<questionIndex>
      const parts = type.split('-');
      const secIdxNum = Number(parts[1]);
      const subSecIdxNum = Number(parts[2]);
      const quesIdxNum = Number(parts[3]);
      if (isNaN(secIdxNum) || isNaN(subSecIdxNum) || isNaN(quesIdxNum)) return;

      const section = qpg_content?.multiSubjectSections[secIdxNum];
      const subSection = section?.multiSubjectSubSections?.[subSecIdxNum];
      const metaData = subSection?.subSectionMetaData;
      const quesMeta = metaData?.[quesIdxNum] as
        | SectionQuestionMetaData
        | undefined;
      if (!section || !subSection || !metaData || !quesMeta) return;

      const updated = cloneObject(qpg_content);
      const sectionCopy = updated?.multiSubjectSections[secIdxNum];
      const subSectionCopy =
        sectionCopy?.multiSubjectSubSections?.[subSecIdxNum];
      const listCopy = subSectionCopy?.subSectionMetaData;
      const quesMetaCopy = listCopy?.[quesIdxNum] as
        | SectionQuestionMetaData
        | undefined;
      if (!listCopy || !quesMetaCopy) return;

      const outerSequence = quesMetaCopy.sequence ?? 0;
      const isGrouped = !!quesMetaCopy.isGrouped;
      const allQuestions = flattenMetaForMutation(quesMetaCopy);
      const [removedAlt] = allQuestions.splice(source.index, 1);
      allQuestions.splice(destination.index, 0, removedAlt);
      listCopy[quesIdxNum] = buildMetaFromDisplayList(
        allQuestions,
        outerSequence,
        isGrouped,
        {
          groupQuestionHeading: quesMetaCopy.groupQuestionHeading,
          groupQuestionAttemptedAny: quesMetaCopy.groupQuestionAttemptedAny,
        }
      );
      // dispatch(setQPGCreatedSectionTest(updated));
      updated.multiSubjectSections[secIdxNum] = sectionCopy;
      sectionCopy.multiSubjectSubSections[subSecIdxNum] = subSectionCopy;

      dispatch(setQPGContent(updated));
    }
  };

  const updateGroupQuestionHeading = (props: {
    sectionIndex: number;
    subSectionIndex: number;
    questionId: string;
    heading: string;
    skipAutosave?: boolean;
  }) => {
    const {
      sectionIndex,
      subSectionIndex,
      questionId,
      heading,
      skipAutosave = true,
    } = props;
    if (!qpg_content?.multiSubjectSections) return undefined;

    if (skipAutosave) {
      skipAutosaveForHeadingRef.current = true;
    }

    const updated = cloneObject(qpg_content);
    const sectionCopy = updated.multiSubjectSections[sectionIndex];
    const subSectionCopy =
      sectionCopy?.multiSubjectSubSections?.[subSectionIndex];
    const metaList = subSectionCopy?.subSectionMetaData;
    if (!sectionCopy || !subSectionCopy || !metaList?.length) return undefined;

    const metaIndex = metaList.findIndex((m) => m.questionId === questionId);
    if (metaIndex < 0) return undefined;

    const existing = metaList[metaIndex];
    const trimmed = heading.trim();
    metaList[metaIndex] = trimmed
      ? new SectionQuestionMetaData({
          ...existing,
          groupQuestionHeading: trimmed,
        })
      : new SectionQuestionMetaData({
          questionId: existing.questionId,
          alternateQuestions: existing.alternateQuestions,
          sequence: existing.sequence,
          showMedia: existing.showMedia,
          isAiGenerated: existing.isAiGenerated,
          maxMarks: existing.maxMarks,
          questionType: existing.questionType,
          groupedQuestions: existing.groupedQuestions,
          ...(existing.isGrouped !== undefined
            ? { isGrouped: existing.isGrouped }
            : {}),
          ...(existing.groupQuestionAttemptedAny !== undefined
            ? {
                groupQuestionAttemptedAny: existing.groupQuestionAttemptedAny,
              }
            : {}),
        });

    sectionCopy.multiSubjectSubSections[subSectionIndex] = subSectionCopy;
    updated.multiSubjectSections[sectionIndex] = sectionCopy;
    dispatch(setQPGContent(updated));

    if (qpg_subsection_data?.[subSectionIndex]) {
      const updatedSubSectionData = qpg_subsection_data.map((sub, i) => {
        if (i !== subSectionIndex) return sub;
        const nextQuestions = sub.subSectionQuestions.map((q, qi) => {
          if (qi !== metaIndex) return q;
          if (!trimmed) {
            return new SubSectionQuestionType({
              questionType: q.questionType,
              noOfQuestion: q.noOfQuestion,
              ...(q.marks !== undefined ? { marks: q.marks } : {}),
              isAlternateEnable: q.isAlternateEnable,
              ...(q.isGrouped ? { isGrouped: true } : {}),
              ...(q.groupQuestionAttemptedAny !== undefined
                ? {
                    groupQuestionAttemptedAny: q.groupQuestionAttemptedAny,
                  }
                : {}),
            });
          }
          return new SubSectionQuestionType({
            ...q,
            groupQuestionHeading: trimmed,
          });
        });
        return new SubSectionTypeV2({
          ...sub,
          subSectionQuestions: nextQuestions,
        });
      });
      dispatch(setQPGSubsectionData(updatedSubSectionData));
    }
    return updated;
  };

  // --------------------------------------------------------------

  const getMarksForQuestionId = (questionId: string): number => {
    const ques = qpgAssessmentQuestions.find((q) => q.questionId == questionId);
    const taskInfo = taskQuestionInfo.find(
      (info) => info.questionId == questionId
    );
    const quesType = ques?.questionType;
    const foundQuesType = qpgQuestionTypeMarks.find(
      (qType) => qType.questionType == quesType
    );
    if (foundQuesType) return foundQuesType.marks ?? 0;
    if (typeof taskInfo?.maxMarks === 'number') return taskInfo.maxMarks;
    const existing =
      ques?.question?.model?.value?.commonQuestionContent?.marks?.[0];
    if (typeof existing === 'number') return existing;
    return 0;
  };

  // --------------------------------------------------------------

  const handleReplaceClick = async (
    sectionIndex: number,
    subSectionIndex: number,
    index: number,
    altIdx: number,
    currentQuestion?: Question,
    allowAiQuestions?: boolean,
    prompts?: string[]
  ) => {
    if (!currentQuestion) return;
    try {
      setUpdatingAssessment(true);

      // Get bloom level and difficulty level from current question
      const questionBloomLevels = currentQuestion.questionMeta?.bloomLevel
        ? [currentQuestion.questionMeta?.bloomLevel]
        : undefined;
      const requiredDifficultyTypes = currentQuestion.questionMeta
        ?.difficultyLevel
        ? [currentQuestion.questionMeta.difficultyLevel]
        : [];

      // Get all module IDs for fallback
      // console.log('qpgAssessmentTask:', qpgAssessmentTask);
      const allModuleIds =
        qpgAssessmentTask.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
          .flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];

      // Get the moduleId from questionModuleMap
      const questionMapEntry = questionModuleMap.find(
        (q) => q.questionId === currentQuestion.questionId
      );
      let moduleQuestions;

      const ignoreIds = new Set<string>([
        ...getUniqueQuestionIdsFromQpgContent({
          qpgContent: qpg_content,
        }),
        ...questionsListWithAlternateQuestions.map(
          (ques) => ques.questionId || ''
        ),
        ...replacedQuestionIds,
      ]);

      const subSectionType =
        qpg_content?.multiSubjectSections[sectionIndex]
          ?.multiSubjectSubSections?.[subSectionIndex]?.subSectionType;

      // Attempt 1: specific moduleId + bloom level of the question
      if (
        questionMapEntry &&
        (questionMapEntry.topicId || questionMapEntry.chapterId)
      ) {
        const specificModuleId =
          questionMapEntry.topicId ?? questionMapEntry.chapterId;
        if (specificModuleId) {
          moduleQuestions =
            await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
              {
                teacherId: user_info?.teacherProfileId,
                subjectId:
                  qpg_content?.multiSubjectSections[sectionIndex].subjectId,
                sectionId: qpg_content?.assessment?.sectionId,
                moduleIds: [specificModuleId],
                assessmentCreationType:
                  AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
                noOfQuestions: 1,
                requiredQuestionTypes: [currentQuestion.questionType],
                requiredDifficultyTypes: requiredDifficultyTypes,
                bloomLevels: questionBloomLevels,
                qIdsToBeIgnored: Array.from(ignoreIds),
                currentQuestionId: currentQuestion.questionId,
                allowAiQuestions: allowAiQuestions,
                aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
                isAiAssessment: allowAiQuestions,
                ...(subSectionType !== undefined ? { subSectionType } : {}),
              }
            );

          // If no questions found, reset to trigger fallback
          if (
            !moduleQuestions.data?.questions ||
            moduleQuestions.data.questions.length === 0
          ) {
            moduleQuestions = undefined;
          }
        }
      }

      // Fallback 1: bloom level of that question with all module ids
      if (
        (!moduleQuestions || !moduleQuestions.data?.questions?.length) &&
        questionBloomLevels
      ) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId:
                qpg_content?.multiSubjectSections[sectionIndex].subjectId,
              sectionId: qpg_content?.assessment?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              bloomLevels: questionBloomLevels,
              qIdsToBeIgnored: Array.from(ignoreIds),
              currentQuestionId: currentQuestion.questionId,
              allowAiQuestions: allowAiQuestions,
              aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
              isAiAssessment: allowAiQuestions,
              ...(subSectionType !== undefined ? { subSectionType } : {}),
            }
          );

        // If no questions found, reset to trigger fallback 2
        if (
          !moduleQuestions.data?.questions ||
          moduleQuestions.data.questions.length === 0
        ) {
          moduleQuestions = undefined;
        }
      }

      // Fallback 2: no bloom level + all module ids
      if (!moduleQuestions || !moduleQuestions.data?.questions?.length) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId:
                qpg_content?.multiSubjectSections[sectionIndex].subjectId,
              sectionId: qpg_content?.assessment?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              qIdsToBeIgnored: Array.from(ignoreIds),
              currentQuestionId: currentQuestion.questionId,
              allowAiQuestions: allowAiQuestions,
              aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
              isAiAssessment: allowAiQuestions,
              ...(subSectionType !== undefined ? { subSectionType } : {}),
            }
          );
      }

      if (moduleQuestions && moduleQuestions.data?.questions?.length) {
        const newQuestion = moduleQuestions.data.questions[0];
        if (
          currentQuestion.questionId &&
          !replacedQuestionIds.includes(currentQuestion.questionId)
        ) {
          setReplacedQuestionIds([
            ...replacedQuestionIds,
            currentQuestion.questionId,
          ]);
        }
        await addOrReplaceQuestion(
          newQuestion,
          sectionIndex,
          subSectionIndex,
          index,
          altIdx,
          moduleQuestions.data?.questionModuleMap
        );
        dispatch(
          setToastInfo({
            label: 'Question replaced successfully',
            variant: 'info',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'No questions found to replace',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      let message = 'Failed to replace question';
      if (err instanceof ConnectError) {
        message = err.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      console.log(err);
      dispatch(
        setToastInfo({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setUpdatingAssessment(false);
    }
  };

  // --------------------------------------------------------------

  const handleShowMediaClick = async (
    sectionIndex: number,
    subSectionIndex: number,
    index: number,
    altIdx: number
  ) => {
    showMediaClick(sectionIndex, subSectionIndex, index, altIdx);
    handleSave({});
  };

  // --------------------------------------------------------------

  const getSubjectName = (subjectId: number) =>
    getTeacherSubjectName(subjectId, user_info?.teachClassSubjects) ?? '';

  // --------------------------------------------------------------

  const subsectionTotalMarks = (subSection: {
    attemptAnyCount?: number;
    subSectionMetaData?: SectionQuestionMetaData[];
    questionTypeAndMarks?: { marks?: number }[];
  }): number =>
    getSubSectionMarksTotal({
      subSection,
      getMarksForQuestionId,
    });

  // const selectedSectionIndex = qpg_selected_section_index ?? 0;
  // const meta = qpg_created_section_test?.multiSubjectSectionMetaInfo;
  // const sectionToShow = meta?.[selectedSectionIndex] ?? meta?.[0];
  // const effectiveSectionIndex =
  //   sectionToShow === meta?.[selectedSectionIndex] ? selectedSectionIndex : 0;

  // console.log('sectionToShow:', sectionToShow);
  const effectiveSectionIndex = (qpg_selected_section_number ?? 1) - 1;
  const multiSubjectSections = qpg_content?.multiSubjectSections ?? [];
  const foundSectionIndex = multiSubjectSections.findIndex(
    (section) => section.sectionNo === sectionToShow?.sectionNo
  );
  const questionSectionIndex =
    foundSectionIndex >= 0 ? foundSectionIndex : 0;

  // --------------------------------------------------------------

  // Reset to first accordion when section changes
  // useEffect(() => {
  //   setExpandedAccordion(0);
  // }, [effectiveSectionIndex]);

  // --------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      {sectionToShow ? (
        <DragDropContext
          key={qpg_selected_section_number + sectionToShow.sectionName}
          onDragEnd={(result) => {
            if (result.type === 'MAIN_BOX') {
              const match =
                result.source.droppableId.match(/section-(\d+)-(\d+)/);
              if (match) {
                onDragEnd(
                  result,
                  parseInt(match[1], 10),
                  parseInt(match[2], 10)
                );
              }
            } else if (result.type.startsWith('ALTERNATE_QUESTION')) {
              onDragEnd(result);
            }
          }}
        >
          <Box sx={styles.subjectSectionDetails}>
            {sectionToShow.sectionTitle ? (
              <Typography
                variant="h2"
                color="primary.main"
                sx={styles.ellipsisText}
              >
                Section {effectiveSectionIndex + 1} -{' '}
                {getSubjectName(sectionToShow.subjectId)} -{' '}
                {sectionToShow.sectionTitle}
              </Typography>
            ) : (
              <Typography
                variant="h2"
                color="primary.main"
                sx={styles.ellipsisText}
              >
                Section {effectiveSectionIndex + 1} -{' '}
                {getSubjectName(sectionToShow.subjectId)}
              </Typography>
            )}
          </Box>

          {sectionToShow.multiSubjectSubSections.map(
            (subSection, subSectionIndex) => {
              const isPassage = isPassageSectionEnabled(subSection);
              return (
                <Accordion
                  key={subSectionIndex + subSection.subSectionName}
                  sx={styles.accordion}
                  expanded={expandedAccordion === subSectionIndex}
                  onChange={(_, isExpanded) =>
                    setExpandedAccordion(isExpanded ? subSectionIndex : -1)
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={styles.accordionSummary}
                  >
                    <Box>
                      <Typography
                        variant="h2"
                        sx={{ fontSize: { xs: pxToRem(24), md: pxTovW(26) } }}
                      >
                        {subSection.subSectionName}
                      </Typography>

                      <Box sx={styles.subSectionDetails}>
                        <Box sx={styles.detailsContainer}>
                          <IconWrapper
                            name="questions"
                            size="md"
                            type="png"
                            parentFolder="icons"
                          />
                          <Typography variant="h4">
                            Total No of Questions:
                            <b> {subSection.subSectionMetaData.length}</b>
                          </Typography>
                        </Box>
                        <Box sx={styles.detailsContainer}>
                          <IconWrapper
                            name="percentage-primary"
                            size="md"
                            type="png"
                            parentFolder="icons"
                          />
                          <Typography variant="h4">
                            Total Marks:{' '}
                            <b>
                              {formatMarksDisplay(
                                subsectionTotalMarks(subSection)
                              )}
                            </b>
                          </Typography>
                        </Box>
                        {subSection.attemptAnyCount !== undefined ? (
                          <Typography variant="h4">
                            ( Attempt any: <b>{subSection.attemptAnyCount}</b> )
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>

                    <Button
                      sx={styles.addMoreButton}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        // console.log('add more:', subSection);
                        // Clear alternate-question context so "ADD FROM QUESTION BANK" adds as new question to this subsection, not as alternate to a previous selection
                        dispatch(
                          setSelectedQuestionIndexForAlternateQuestion(
                            undefined
                          )
                        );
                        dispatch(
                          setSelectedQuestionTypeForAlternateQuestion(undefined)
                        );
                        dispatch(
                          setQPGSelectedSubSectionIndex(subSectionIndex)
                        );
                        navigate(QPG_QUESTION_BANK, {
                          replace: true,
                          state: {
                            from: location.pathname,
                            redirectionPath: location.state?.redirectionPath,
                          },
                        });
                      }}
                    >
                      <AddCircleIcon
                        sx={{
                          color: '#03612D',
                          fontSize: { xs: pxToRem(18), md: pxTovW(24) },
                        }}
                      />
                      <Typography
                        variant="smallText"
                        fontWeight="bold"
                        color="secondary.main"
                      >
                        ADD FROM QUESTION BANK
                      </Typography>
                    </Button>
                  </AccordionSummary>

                  <AccordionDetails sx={styles.accordionDetails}>
                    {(() => {
                      const passageImageSrc = getCloudUploadPreviewSrc(
                        subSection.passageImagesUpload?.[0]
                      );
                      const passageBody = (subSection.passageText ?? [])
                        .map((t) => t?.trim())
                        .filter(Boolean)
                        .join('\n\n');
                      if (!isPassage || (!passageImageSrc && !passageBody)) {
                        return null;
                      }
                      return (
                        <Box sx={styles.passageBlock}>
                          {passageBody ? (
                            <Typography variant="h4" sx={styles.passageText}>
                              {passageBody}
                            </Typography>
                          ) : null}
                          {passageImageSrc ? (
                            <Box
                              component="img"
                              src={passageImageSrc}
                              alt="Passage"
                              sx={styles.passageImage}
                            />
                          ) : null}
                        </Box>
                      );
                    })()}
                    {subSection.attemptAnyCount !== undefined ? (
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          marginBottom: { xs: pxToRem(8), md: pxTovW(8) },
                        }}
                      >
                        {getSubSectionAttemptAnyInstruction(
                          subSection.attemptAnyCount
                        )}
                      </Typography>
                    ) : null}
                    <Droppable
                      droppableId={`section-${effectiveSectionIndex}-${subSectionIndex}`}
                      type="MAIN_BOX"
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { md: pxTovW(40), xs: pxToRem(15) },
                          }}
                        >
                          {[...(subSection.subSectionMetaData ?? [])]
                            .sort(
                              (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
                            )
                            .map((ques, index) => {
                              const questionNumber =
                                getQuestionNumberFromMultiSubject({
                                  sectionIndex: questionSectionIndex,
                                  subSectionIndex,
                                  questionIndexInSubSection: index,
                                  multiSubjectSections,
                                });
                              const isGrouped = !!ques.isGrouped;
                              const allQuestions =
                                getDisplayQuestionsForMeta(ques);
                              const questions = allQuestions.map((quesMeta) => {
                                const q = getFormattedQuestionFromId(
                                  quesMeta.questionId
                                );
                                const quesType = q?.questionType;
                                const subSectionMarks = quesType
                                  ? subSection.questionTypeAndMarks.find(
                                      (val) => val.questionType == quesType
                                    )
                                  : undefined;
                                if (
                                  q?.question?.model.value
                                    ?.commonQuestionContent
                                ) {
                                  if (quesMeta.maxMarks) {
                                    q.question.model.value.commonQuestionContent.marks =
                                      [quesMeta.maxMarks];
                                  } else if (subSectionMarks) {
                                    q.question.model.value.commonQuestionContent.marks =
                                      [subSectionMarks.marks];
                                  }
                                }
                                return q;
                              });
                              if (questions.length === 0) {
                                return null;
                              }
                              const groupedHeaderLabel = isGrouped
                                ? getGroupedQuestionHeaderLabel({
                                    questionNumber,
                                    meta: ques,
                                    questionType:
                                      ques.questionType ??
                                      allQuestions[0]?.questionType ??
                                      questions[0]?.questionType,
                                  })
                                : undefined;
                              // Level 1 Draggable
                              return (
                                <Draggable
                                  key={ques.questionId}
                                  draggableId={ques.questionId}
                                  index={index}
                                  isDragDisabled={
                                    taskCreationStatus ===
                                    TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                                  }
                                >
                                  {(provided) => {
                                    const firstQuestion = allQuestions[0];
                                    const showMedia = firstQuestion.showMedia;
                                    const questionData =
                                      qpg_content?.assessmentContent.find(
                                        (q) => q.questionId === ques.questionId
                                      );
                                    return (
                                      <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                      >
                                        {questions.length === 1 ? (
                                          <QuestionContainerWithSolution
                                            questionNumber={questionNumber}
                                            hideImage={
                                              taskCreationStatus ===
                                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                              !showMedia
                                            }
                                            headerLeftElement={
                                              <QuestionSourceTags
                                                question={questionData}
                                                teacherProfileId={
                                                  user_info?.teacherProfileId
                                                }
                                                teacherName={
                                                  user_info?.userName || ''
                                                }
                                              />
                                            }
                                            headerRightElement={
                                              taskCreationStatus !==
                                              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                <Box
                                                  {...provided.dragHandleProps}
                                                  sx={{ flex: 1 }}
                                                >
                                                  <InteractiveButtons
                                                    stackComponents={isMobile}
                                                    showAIButton={
                                                      questions[0]
                                                        ?.isAiGenerated
                                                    }
                                                    handleAIPrompt={(
                                                      prompt
                                                    ) => {
                                                      handleReplaceClick(
                                                        effectiveSectionIndex,
                                                        subSectionIndex,
                                                        index,
                                                        0,
                                                        questions[0],
                                                        true,
                                                        [prompt]
                                                      );
                                                    }}
                                                    isIncludeImageChecked={
                                                      showMedia
                                                    }
                                                    showIncludeImageButton={questionHasImage(
                                                      questions[0]
                                                    )}
                                                    handleIncludeImageClick={() => {
                                                      handleShowMediaClick(
                                                        effectiveSectionIndex,
                                                        subSectionIndex,
                                                        index,
                                                        0
                                                      );
                                                    }}
                                                    {...(!isGrouped
                                                      ? {
                                                          addAlternateQuestionsClick:
                                                            () => {
                                                              dispatch(
                                                                setQPGSelectedSubSectionIndex(
                                                                  subSectionIndex
                                                                )
                                                              );
                                                              dispatch(
                                                                setSelectedQuestionIndexForAlternateQuestion(
                                                                  index
                                                                )
                                                              );
                                                              dispatch(
                                                                setSelectedQuestionTypeForAlternateQuestion(
                                                                  questions[0]
                                                                    ?.questionType
                                                                )
                                                              );
                                                              navigate(
                                                                QPG_QUESTION_BANK,
                                                                {
                                                                  replace: true,
                                                                  state: {
                                                                    from: location.pathname,
                                                                    redirectionPath:
                                                                      location
                                                                        .state
                                                                        ?.redirectionPath,
                                                                  },
                                                                }
                                                              );
                                                            },
                                                        }
                                                      : {})}
                                                    onDeleteClick={() => {
                                                      setDeleteQuestionPopupState(
                                                        {
                                                          sectionIndex:
                                                            effectiveSectionIndex,
                                                          subSectionIndex,
                                                          questionIndex: index,
                                                          altIdx: 0,
                                                        }
                                                      );
                                                    }}
                                                    {...(!isPassage
                                                      ? {
                                                          handleReplaceClick:
                                                            () =>
                                                              handleReplaceClick(
                                                                effectiveSectionIndex,
                                                                subSectionIndex,
                                                                index,
                                                                0,
                                                                questions[0],
                                                                false
                                                              ),
                                                        }
                                                      : {})}
                                                  />
                                                </Box>
                                              ) : (
                                                // Published state
                                                <Box
                                                  {...provided.dragHandleProps}
                                                ></Box>
                                              )
                                            }
                                            question={questions[0]}
                                            showQuestionStats
                                            showAnswer
                                            imageResizeEnabled={true}
                                            key={'ques_' + index}
                                          />
                                        ) : (
                                          <Box
                                            sx={{
                                              border: `1px solid #007CDC`,
                                              borderRadius: {
                                                xs: pxToRem(20),
                                                md: pxTovW(20),
                                              },
                                              padding: {
                                                xs: `${pxTovW(14)} ${pxTovW(
                                                  10
                                                )}`,
                                                md: `${pxTovW(18)} ${pxTovW(
                                                  13
                                                )}`,
                                              },
                                              backgroundColor: '#F5F5FF',
                                              display: 'flex',
                                              flexDirection: 'column',
                                            }}
                                            key={'alternate_ques_' + index}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                              }}
                                              {...provided.dragHandleProps}
                                            >
                                              {isGrouped &&
                                              groupedHeaderLabel ? (
                                                taskCreationStatus !==
                                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                  <GroupedHeaderEditor
                                                    questionNumber={questionNumber}
                                                    initialBody={getGroupedQuestionHeaderBody(
                                                      {
                                                        meta: ques,
                                                        questionType:
                                                          ques.questionType ??
                                                          allQuestions[0]
                                                            ?.questionType ??
                                                          questions[0]
                                                            ?.questionType,
                                                      }
                                                    )}
                                                    onSave={async (
                                                      nextBody
                                                    ) => {
                                                      const updatedContent =
                                                        updateGroupQuestionHeading(
                                                          {
                                                            sectionIndex:
                                                              effectiveSectionIndex,
                                                            subSectionIndex,
                                                            questionId:
                                                              ques.questionId,
                                                            heading: nextBody,
                                                            skipAutosave: true,
                                                          }
                                                        );
                                                      await handleSave({
                                                        ...(updatedContent
                                                          ? {
                                                              contentOverride:
                                                                updatedContent,
                                                            }
                                                          : {}),
                                                      });
                                                    }}
                                                  />
                                                ) : (
                                                  <Typography
                                                    variant="h5"
                                                    fontWeight="bold"
                                                    sx={
                                                      styles.groupedHeaderReadOnly
                                                    }
                                                  >
                                                    {groupedHeaderLabel}
                                                  </Typography>
                                                )
                                              ) : null}
                                              {taskCreationStatus !==
                                              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                <Box
                                                  sx={{
                                                    ml: 'auto',
                                                    pr: {
                                                      xs: pxToRem(10),
                                                      md: pxTovW(15),
                                                    },
                                                    pb: {
                                                      xs: pxToRem(6),
                                                      md: pxTovW(10),
                                                    },
                                                    pt: {
                                                      xs: pxToRem(6),
                                                      md: pxTovW(0),
                                                    },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: pxTovW(14),
                                                  }}
                                                >
                                                  {/* Blue outer box */}
                                                  <InteractiveButtons
                                                    {...(!isGrouped
                                                      ? {
                                                          addAlternateQuestionsClick:
                                                            () => {
                                                              dispatch(
                                                                setQPGSelectedSubSectionIndex(
                                                                  subSectionIndex
                                                                )
                                                              );
                                                              dispatch(
                                                                setSelectedQuestionIndexForAlternateQuestion(
                                                                  index
                                                                )
                                                              );
                                                              dispatch(
                                                                setSelectedQuestionTypeForAlternateQuestion(
                                                                  questions[0]
                                                                    ?.questionType
                                                                )
                                                              );
                                                              navigate(
                                                                QPG_QUESTION_BANK,
                                                                {
                                                                  replace: true,
                                                                  state: {
                                                                    from: location.pathname,
                                                                    redirectionPath:
                                                                      location
                                                                        .state
                                                                        ?.redirectionPath,
                                                                  },
                                                                }
                                                              );
                                                            },
                                                        }
                                                      : {})}
                                                    onDeleteClick={() => {
                                                      setDeleteQuestionPopupState(
                                                        {
                                                          sectionIndex:
                                                            effectiveSectionIndex,
                                                          subSectionIndex,
                                                          questionIndex: index,
                                                        }
                                                      );
                                                    }}
                                                  />
                                                </Box>
                                              ) : (
                                                !isGrouped && (
                                                  <Box
                                                    {...provided.dragHandleProps}
                                                  />
                                                )
                                              )}
                                            </Box>
                                            {/* Level 2 DragDrop for main + alternates */}
                                            <Droppable
                                              droppableId={`alternates-${effectiveSectionIndex}-${subSectionIndex}-${index}`}
                                              type={`ALTERNATE_QUESTION-${effectiveSectionIndex}-${subSectionIndex}-${index}`}
                                              direction="vertical"
                                            >
                                              {(altProvided) => {
                                                return (
                                                  <Box
                                                    ref={altProvided.innerRef}
                                                    {...altProvided.droppableProps}
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: {
                                                        md: pxTovW(20),
                                                        xs: pxToRem(10),
                                                      },
                                                    }}
                                                  >
                                                    {allQuestions.map(
                                                      (ques, altIdx, arr) => {
                                                        const qid =
                                                          ques.questionId;
                                                        const question =
                                                          questions.find(
                                                            (val) =>
                                                              val?.questionId ==
                                                              qid
                                                          );
                                                        const isAiGenerated =
                                                          question?.isAiGenerated;
                                                        const showMedia =
                                                          ques.showMedia;
                                                        return (
                                                          <React.Fragment
                                                            key={qid}
                                                          >
                                                            <Draggable
                                                              key={qid}
                                                              draggableId={`${qid}-alt`}
                                                              index={altIdx}
                                                              isDragDisabled={
                                                                taskCreationStatus ===
                                                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                                                              }
                                                            >
                                                              {(
                                                                altDraggable
                                                              ) => (
                                                                <Box
                                                                  ref={
                                                                    altDraggable.innerRef
                                                                  }
                                                                  {...altDraggable.draggableProps}
                                                                >
                                                                  <QuestionContainerWithSolution
                                                                    questionNumber={
                                                                      isGrouped
                                                                        ? toRomanNumeral(
                                                                            altIdx +
                                                                              1
                                                                          )
                                                                        : questionNumber
                                                                    }
                                                                    hideImage={
                                                                      taskCreationStatus ===
                                                                        TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                                                      !showMedia
                                                                    }
                                                                    headerLeftElement={
                                                                      <QuestionSourceTags
                                                                        question={
                                                                          questionData
                                                                        }
                                                                        teacherProfileId={
                                                                          user_info?.teacherProfileId
                                                                        }
                                                                        teacherName={
                                                                          user_info?.userName ||
                                                                          ''
                                                                        }
                                                                      />
                                                                    }
                                                                    headerRightElement={
                                                                      taskCreationStatus !==
                                                                      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                                        <Box
                                                                          {...altDraggable.dragHandleProps}
                                                                          sx={{
                                                                            flex: 1,
                                                                          }}
                                                                        >
                                                                          <InteractiveButtons
                                                                            stackComponents={
                                                                              isMobile
                                                                            }
                                                                            showAIButton={
                                                                              isAiGenerated
                                                                            }
                                                                            handleAIPrompt={(
                                                                              prompt
                                                                            ) => {
                                                                              handleReplaceClick(
                                                                                effectiveSectionIndex,
                                                                                subSectionIndex,
                                                                                index,
                                                                                altIdx,
                                                                                question,
                                                                                true,
                                                                                [
                                                                                  prompt,
                                                                                ]
                                                                              );
                                                                            }}
                                                                            showIncludeImageButton={questionHasImage(
                                                                              question
                                                                            )}
                                                                            handleIncludeImageClick={() => {
                                                                              handleShowMediaClick(
                                                                                effectiveSectionIndex,
                                                                                subSectionIndex,
                                                                                index,
                                                                                altIdx
                                                                              );
                                                                            }}
                                                                            isIncludeImageChecked={
                                                                              showMedia
                                                                            }
                                                                            {...(!isGrouped
                                                                              ? {
                                                                                  onDeleteClick:
                                                                                    () => {
                                                                                      setDeleteQuestionPopupState(
                                                                                        {
                                                                                          sectionIndex:
                                                                                            effectiveSectionIndex,
                                                                                          subSectionIndex,
                                                                                          questionIndex:
                                                                                            index,
                                                                                          altIdx:
                                                                                            altIdx,
                                                                                        }
                                                                                      );
                                                                                    },
                                                                                }
                                                                              : {})}
                                                                            {...(!isPassage
                                                                              ? {
                                                                                  handleReplaceClick:
                                                                                    () =>
                                                                                      handleReplaceClick(
                                                                                        effectiveSectionIndex,
                                                                                        subSectionIndex,
                                                                                        index,
                                                                                        altIdx,
                                                                                        question,
                                                                                        false
                                                                                      ),
                                                                                }
                                                                              : {})}
                                                                          />
                                                                        </Box>
                                                                      ) : (
                                                                        // Published state
                                                                        <Box
                                                                          {...altDraggable.dragHandleProps}
                                                                        />
                                                                      )
                                                                    }
                                                                    question={
                                                                      question
                                                                    }
                                                                    showQuestionStats
                                                                    showAnswer
                                                                    imageResizeEnabled={
                                                                      true
                                                                    }
                                                                    key={
                                                                      'ques_' +
                                                                      index +
                                                                      '-' +
                                                                      altIdx
                                                                    }
                                                                  />
                                                                </Box>
                                                              )}
                                                            </Draggable>
                                                            {altIdx <
                                                              arr.length - 1 &&
                                                              !isGrouped &&
                                                              renderOrBox()}
                                                          </React.Fragment>
                                                        );
                                                      }
                                                    )}
                                                    {altProvided.placeholder}
                                                  </Box>
                                                );
                                              }}
                                            </Droppable>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </AccordionDetails>
                </Accordion>
              );
            }
          )}
        </DragDropContext>
      ) : null}

      <ActionsPopup
        open={!!deleteQuestionPopupState}
        fontSmall={true}
        iconName="delete"
        popupText="Are you sure you want to delete this question"
        handleClose={() => setDeleteQuestionPopupState(undefined)}
        noClickHandler={() => setDeleteQuestionPopupState(undefined)}
        yesClickHandler={async () => {
          if (!deleteQuestionPopupState) {
            setDeleteQuestionPopupState(undefined);
            return;
          }
          const {
            sectionIndex: secIdx,
            subSectionIndex: subSecIdx,
            questionIndex: qIdx,
            altIdx: aIdx,
          } = deleteQuestionPopupState;
          if (typeof qIdx !== 'number') {
            setDeleteQuestionPopupState(undefined);
            return;
          }
          if (aIdx !== undefined) {
            deleteQuestion(secIdx, subSecIdx, qIdx, aIdx);
          } else {
            deleteQuestion(secIdx, subSecIdx, qIdx);
          }
          dispatch(
            setToastInfo({
              label: 'Question deleted successfully',
              variant: 'info',
              open: true,
            })
          );
          setDeleteQuestionPopupState(undefined);
        }}
      />
    </Box>
  );
};

// =====================================================

const renderOrBox = () => {
  return (
    <Box sx={{ px: { xs: pxToRem(10), md: pxTovW(20) } }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#DDF0FF',
            color: '#000',
            padding: '10px',
            border: '1px solid #007CDC',
            borderRadius: '50%',
            zIndex: 1,
          }}
        >
          OR
        </Box>

        <Box
          sx={{
            height: '1px',
            width: '100%',
            backgroundColor: '#007CDC',
            position: 'absolute',
            top: '50%',
            left: '0',
            right: '0',
            transform: 'translateY(-50%)',
          }}
        />
      </Box>
    </Box>
  );
};

// =====================================================
